import React, { useState, useEffect, useCallback } from 'react';
import { fetchQueryById, deleteVersion } from '../api';
import Spinner from '../components/Spinner';
import QueryInfoCard from '../components/QueryInfoCard';
import VersionTableRow from '../components/VersionTableRow';
import AddVersionForm from '../components/AddVersionForm';
import SqlEditModal from '../components/SqlEditModal';
import ConfirmModal from '../components/ConfirmModal';
import ErrorBanner from '../components/ErrorBanner';

const CARD = { backgroundColor: '#14161e', border: '1px solid #1e2130', borderRadius: '16px' };
const VERSION_COLS = [{ label: 'Added', w: '200px' }, { label: 'SQL Preview', w: 'auto' }, { label: '', w: '160px' }];

export default function QueryDetail({ id, onBack }) {

  const [query, setQuery]               = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [editingVersion, setEditingVersion] = useState(null);
  const [deletingVersion, setDeletingVersion] = useState(null);
  const [confirmDeleteVersion, setConfirmDeleteVersion] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setQuery(await fetchQueryById(id)); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function handleDeleteVersionConfirmed() {
    const versionId = confirmDeleteVersion;
    setConfirmDeleteVersion(null);
    setDeletingVersion(versionId);
    setDeleteError(null);
    try {
      await deleteVersion(id, versionId);
      setQuery(q => ({ ...q, versions: q.versions.filter(v => v.id !== versionId) }));
    } catch (err) {
      setDeleteError(err.message);
      setTimeout(() => setDeleteError(null), 3000);
    } finally {
      setDeletingVersion(null);
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center" style={{ minHeight: '100vh' }}>
      <div className="flex items-center gap-2" style={{ color: '#4b5563' }}>
        <Spinner className="w-5 h-5" />
        <span className="text-sm">Loading…</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center" style={{ minHeight: '100vh' }}>
      <p className="text-sm" style={{ color: '#ef4444' }}>{error}</p>
    </div>
  );

  if (!query) return null;

  return (
    <div className="p-8" style={{ maxWidth: '960px', width: '100%' }}>

      {/* Back */}
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm mb-6 transition-colors"
        style={{ color: '#4b5563' }}
        onMouseEnter={e => e.currentTarget.style.color = '#9ca3af'}
        onMouseLeave={e => e.currentTarget.style.color = '#4b5563'}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back to Queries
      </button>

      {/* Info */}
      <div className="p-6 mb-5" style={CARD}>
        <QueryInfoCard query={query} onUpdated={updated => setQuery(q => ({ ...q, ...updated }))} />
      </div>

      {/* Versions */}
      <div className="mb-5 overflow-hidden" style={CARD}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid #1e2130' }}>
          <h2 className="text-sm font-semibold text-gray-200">Version History</h2>
          <span className="text-xs font-mono px-2 py-0.5 rounded-md" style={{ backgroundColor: '#1e2130', color: '#818cf8' }}>
            {(query.versions || []).length}
          </span>
        </div>
        {(query.versions || []).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 gap-2" style={{ color: '#374151' }}>
            <p className="text-sm">No versions yet — add one below</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: '#0f1117', borderBottom: '1px solid #1e2130' }}>
                {VERSION_COLS.map(({ label, w }, i) => (
                  <th key={i} className="text-left px-6 py-3.5"
                    style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#4b5563', width: w }}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(query.versions || []).map(v => (
                <VersionTableRow
                  key={v.id} version={v}
                  onEdit={() => setEditingVersion(v)}
                  onDelete={() => setConfirmDeleteVersion(v.id)}
                  deleting={deletingVersion === v.id}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add version */}
      <div className="p-6" style={CARD}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)' }}>
            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </div>
          <h2 className="text-sm font-semibold text-gray-200">Add New Version</h2>
        </div>
        <AddVersionForm queryId={id} onAdded={load} />
      </div>

      {editingVersion && (
        <SqlEditModal
          version={editingVersion} queryId={id}
          onSave={load} onClose={() => setEditingVersion(null)}
        />
      )}

      {confirmDeleteVersion && (
        <ConfirmModal
          title="Delete Version"
          message="Delete this version? This cannot be undone."
          onConfirm={handleDeleteVersionConfirmed}
          onClose={() => setConfirmDeleteVersion(null)}
        />
      )}

      {deleteError && (
        <div className="fixed bottom-6 right-6 z-50 w-80">
          <ErrorBanner message={deleteError} />
        </div>
      )}
    </div>
  );
}
